import React, { ReactNode, useEffect, useMemo } from 'react';

import useInterval from '@use-it/interval';

import { TOOL_IDS, useCurrentProject } from '@adsk/alloy-provider';
import { noop } from '@adsk/alloy-react-helpers';

import { isPluginProductOrToolNotAvailableInEntitlements } from './helpers/isPluginProductOrToolNotAvailableInEntitlements';
import use3LORelationships, {
  Add3LORelationshipsPromiseResponseType,
} from './hooks/use3LORelationships';
import { usePluginsValidator } from './hooks/usePluginsValidator';
import { useEntitiesCacheContext } from './providers/EntitiesCacheProvider';
import { useReferencePickerPlugins } from './providers/ReferencePickerPluginsProvider';
import { useRelationshipsContext } from './providers/RelationshipsProvider';
import ReferencePickerView from './ReferencePickerView';
import { CommonReferencePickerPlugin } from './types/ReferencePickerPlugin';
import {
  AddRelationships,
  CreateFormProps,
  Entity,
  EntityRelationship,
  Relationship,
} from './types/types';

export interface ConnectedReferencePickerProps {
  style?: React.CSSProperties;
  className?: string;
  displayVariant?: string;
  sourceEntityId: string;
  sourceEntityDomain: string;
  sourceEntityType: string;
  targetPlugins?: CommonReferencePickerPlugin[];
  container?: React.RefObject<HTMLElement>;
  allowAddingLinks?: boolean;
  allowRemoveLinkedEntity?: boolean | ((args: EntityRelationship) => boolean);
  onRemove: (args: Relationship) => Promise<void | string[]>;
  onAddRelationships: AddRelationships;
  title?: string;
  addButtonText?: string;
  emptyStateText?: ReactNode;
  createFormProps?:
    | CreateFormProps
    | ((args: { domain: string; type: string }) => CreateFormProps);
  dataViewProps?:
    | Record<string, unknown>
    | ((args: { domain: string; type: string }) => Record<string, unknown>);
  onAddReferenceClick?: (args: { domain: string; type: string }) => void;
  onReferenceClickAnalytics?: (args: unknown) => void;
  loadingPlugins?: boolean;
  disableRelationshipService?: boolean;
  pollingInterval?: number;
  allow3LOAdd?: boolean;
  allow3LORemove?: boolean;
  onAddSuccess?: (args: Relationship[]) => void;
  onRemoveSuccess?: (args: string[], relationship: Relationship) => void;
  onAddFailure?: (args: unknown) => void;
  onRemoveFailure?: (args: Error, relationship: Relationship) => void;
}

/**
 * A connected ReferencePicker component.
 *
 * This component allows you to use `ConnectedReferencePicker` somewhere under a `ReferencePickerProvider` component.
 * It's useful when you have other elements on screen that needs to share the same relationship data with this component.
 *
 * This component expects `ReferencePickerProvider` somewhere above it in the components tree.
 */
const ConnectedReferencePicker = React.forwardRef<
  HTMLDivElement,
  ConnectedReferencePickerProps
>(
  (
    {
      onAddRelationships,
      onRemove,
      disableRelationshipService,
      allow3LOAdd,
      allow3LORemove,
      onAddSuccess = noop,
      onAddFailure = noop,
      onRemoveSuccess = noop,
      onRemoveFailure = noop,
      ...props
    },
    ref,
  ) => {
    const {
      sourceEntityId,
      sourceEntityDomain,
      sourceEntityType,
      pollingInterval,
      targetPlugins: pluginsFromProp,
    } = props;

    const currentProject = useCurrentProject();
    const { loadingPlugins, allowedPlugins } = useReferencePickerPlugins();

    usePluginsValidator({
      targetPlugins: pluginsFromProp,
      source: 'ConnectedReferencePicker',
    });

    const pluginsToUse = useMemo(() => {
      if (pluginsFromProp) {
        return pluginsFromProp.filter(
          (plugin) =>
            currentProject &&
            !isPluginProductOrToolNotAvailableInEntitlements({
              plugin,
              currentProject,
              bypassToolIds: [TOOL_IDS.PHOTOS],
            }),
        );
      }
      return pluginsFromProp || allowedPlugins;
    }, [allowedPlugins, pluginsFromProp, currentProject]);

    const sourceIdAsArr = useMemo(
      () => (sourceEntityId ? [sourceEntityId] : []),
      [sourceEntityId],
    );
    const {
      relationshipsEntityMap,
      removeRelationshipById,
      relationshipDataBySource,
      fetchByIds,
      addRelationships,
    } = useRelationshipsContext();
    const { entitiesCache, addEntitiesToCache, updateEntitiesInCache } =
      useEntitiesCacheContext();
    const { is3LOSupported, add3LORelationships, remove3LORelationshipById } =
      use3LORelationships(pluginsToUse);

    useEffect(() => {
      fetchByIds(sourceIdAsArr, true);
    }, [sourceIdAsArr, sourceEntityDomain, sourceEntityType, fetchByIds]);

    useInterval(() => {
      fetchByIds(sourceIdAsArr, true);
    }, pollingInterval ?? null);

    const currentSourceRelationshipData = relationshipDataBySource?.[
      sourceEntityDomain
    ]?.[sourceEntityType]?.[sourceEntityId] || {
      loading: !disableRelationshipService,
      relationshipsData: [],
    };

    const relationshipData = useMemo(
      () => ({
        data: currentSourceRelationshipData.relationshipsData.map(
          ({ relationshipId, domain, type, entityId }) => ({
            domain,
            type,
            entityId,
            relationship: relationshipsEntityMap[relationshipId],
          }),
        ),
        loading: currentSourceRelationshipData.loading,
        error: currentSourceRelationshipData.error,
        retry: currentSourceRelationshipData.retry,
      }),
      // We don't want to calculate data every time `relationshipsEntityMap` is updated.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [currentSourceRelationshipData],
    );

    const onAddRelationshipsWrapper = (
      sourceEntity: Entity,
      entitiesToLink: Entity[],
    ) => {
      if (allow3LOAdd && is3LOSupported(entitiesToLink[0])) {
        return add3LORelationships(sourceEntity, entitiesToLink, {
          includeFailedRelationships: true,
        }).then((result) => {
          const relationships = result as
            | Add3LORelationshipsPromiseResponseType
            | Partial<Add3LORelationshipsPromiseResponseType>;

          if (relationships.fulfilled) {
            onAddSuccess(relationships.fulfilled);
          }

          if (relationships.rejected) {
            onAddFailure(relationships.rejected);
          }

          return relationships?.fulfilled ?? [];
        });
      }

      return onAddRelationships(sourceEntity, entitiesToLink).then(
        (newRelationships) => {
          addRelationships(newRelationships);
          return newRelationships;
        },
      );
    };

    const onRemoveWrapper = (relationship: Relationship) => {
      const entity = relationship.entities.find(
        (entity) =>
          !(
            entity.id === sourceEntityId &&
            entity.domain === sourceEntityDomain &&
            entity.type === sourceEntityType
          ),
      );

      if (
        allow3LORemove &&
        entity &&
        is3LOSupported({ domain: entity.domain, type: entity.type })
      ) {
        return remove3LORelationshipById(relationship.id)
          .then((res) => {
            onRemoveSuccess(res, relationship);
          })
          .catch((err) => {
            onRemoveFailure(err, relationship);
          });
      }

      return onRemove(relationship).then((res) => {
        removeRelationshipById(relationship.id);
        return res;
      });
    };

    return (
      <ReferencePickerView
        {...props}
        targetPlugins={pluginsToUse}
        onAddRelationships={onAddRelationshipsWrapper}
        onRemove={onRemoveWrapper}
        ref={ref}
        relationshipsData={relationshipData}
        entitiesCache={entitiesCache}
        addEntitiesToCache={addEntitiesToCache}
        updateEntitiesInCache={updateEntitiesInCache}
        loadingPlugins={loadingPlugins}
      />
    );
  },
);

ConnectedReferencePicker.displayName = 'ConnectedReferencePicker';

export default ConnectedReferencePicker;
